import {
    applyMZZoneNumberColors,
    MANAGEMENT_ZONE_FILL_LAYER,
    MANAGEMENT_ZONE_LINE_LAYER,
    MANAGEMENT_ZONE_SOURCE,
} from "@/lib/map/layers";
import SimpleLayer from "./SimpleLayer";
import { useCallback, useEffect, useMemo, useState } from "react";
import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { Select, Selection, SelectItem } from "@heroui/react";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    moveLayersToTop,
} from "@/lib/utils/map";
import useMap from "@/lib/map/useMap";
import { Zone } from "@/types/managementZones";
import { FeatureCollection, MultiPolygon } from "geojson";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function ManagementZoneLayers() {
    const { managementZonesQuery } = useFieldDashboard() ?? {};
    const { useGetRequest, downloadFile } = useRequest();
    const { map, mapLoaded } = useMap();

    const primaryManagementZone = useMemo(
        () => managementZonesQuery?.data?.find((layer) => layer.is_primary),
        [managementZonesQuery?.data],
    );

    const [selectedManagementZone, setSelectedManagementZone] =
        useState<Selection>(new Set());

    useEffect(() => {
        if (primaryManagementZone) {
            setSelectedManagementZone(
                new Set([primaryManagementZone.id.toString()]),
            );
        }
    }, [primaryManagementZone]);

    const selectedManagementZoneData = useGetRequest<{
        zones: FeatureCollection<MultiPolygon, Zone>;
    }>({
        url: `management_zones/${selectedManagementZone != "all" ? selectedManagementZone.values().next().value : "all"}/`,
        enabled: selectedManagementZone !== "all",
    });

    const onDownloadManagementZones = useCallback(async () => {
        if (selectedManagementZone === "all") return;

        await downloadFile(
            `management_zones/${selectedManagementZone}/download/`,
            managementZonesQuery?.data?.find(
                (managementZone) =>
                    managementZone.id === Number(selectedManagementZone),
            )?.name ?? "",
        );
    }, [downloadFile, selectedManagementZone, managementZonesQuery?.data]);

    const zonesWithColors = useMemo(() => {
        const zones = selectedManagementZoneData.data?.zones;
        if (!zones) return undefined;
        return applyMZZoneNumberColors(zones);
    }, [selectedManagementZoneData.data?.zones]);

    useEffect(() => {
        if (
            !map ||
            !mapLoaded ||
            !zonesWithColors ||
            selectedManagementZone === "all"
        )
            return;

        addUpdateGeojsonSource(map, MANAGEMENT_ZONE_SOURCE, {
            type: "geojson",
            data: zonesWithColors,
        });

        addUpdateGeojsonLayers(map, [
            MANAGEMENT_ZONE_FILL_LAYER,
            MANAGEMENT_ZONE_LINE_LAYER,
        ]);

        const fillId = MANAGEMENT_ZONE_FILL_LAYER.id;
        moveLayersToTop(map, [fillId, MANAGEMENT_ZONE_LINE_LAYER.id]);
    }, [map, mapLoaded, zonesWithColors, selectedManagementZone]);

    return (
        <>
            <SimpleLayer
                name="Management Zones"
                layerNames={[
                    MANAGEMENT_ZONE_FILL_LAYER.id,
                    MANAGEMENT_ZONE_LINE_LAYER.id,
                ]}
                disabled={selectedManagementZone === "all"}
                onDownload={onDownloadManagementZones}
            >
                {() => (
                    <div className="flex flex-row items-center gap-2">
                        <Select
                            aria-label="Select Management Zone"
                            selectedKeys={selectedManagementZone}
                            onSelectionChange={setSelectedManagementZone}
                            size="sm"
                            renderValue={() => {
                                const value =
                                    selectedManagementZone !== "all"
                                        ? managementZonesQuery?.data?.find(
                                            (managementZone) =>
                                                managementZone.id ===
                                                Number(
                                                    selectedManagementZone
                                                        .values()
                                                        .next().value,
                                                ),
                                        )
                                        : undefined;
                                return (
                                    <div className="flex flex-row items-center gap-2">
                                        {value?.is_primary && (
                                            <FontAwesomeIcon
                                                icon={faStar}
                                                className="text-yellow-500"
                                            />
                                        )}
                                        {selectedManagementZone !== "all" &&
                                            managementZonesQuery?.data?.find(
                                                (managementZone) =>
                                                    managementZone.id ===
                                                    Number(
                                                        selectedManagementZone
                                                            .values()
                                                            .next().value,
                                                    ),
                                            )?.name}
                                    </div>
                                );
                            }}
                        >
                            {(managementZonesQuery?.data ?? []).map(
                                (managementZone) => (
                                    <SelectItem
                                        key={managementZone.id}
                                        startContent={
                                            managementZone.is_primary && (
                                                <FontAwesomeIcon
                                                    icon={faStar}
                                                    className="text-yellow-500"
                                                />
                                            )
                                        }
                                    >
                                        {managementZone.name}
                                    </SelectItem>
                                ),
                            )}
                        </Select>
                    </div>
                )}
            </SimpleLayer>
        </>
    );
}

export default ManagementZoneLayers;
